import { useEffect, useState } from 'react';

import { isLaunchDateUpcoming } from '../components/Containers/PurchaseForm/utils';
import { useWeb3Cache } from '../components/Web3/Web3Provider/useWeb3Cache';
import { nftsGateway } from '../gateways/NftsApi/NftsApi';
import { AttributeInput } from '../types/Purchasing';
import { isSameChainId } from '../utils/web3/chains';
import { useUpdatableAsyncState } from './web3/useUpdatableAsyncState';

export type ReleaseGatingController = {
  product?: ProductType;
  gatedRelease?: GatedRelease;
  requiresNftGating: boolean;
  passedGating: boolean;
  gatingLoading: boolean;
  onWrongChain: boolean;
  shouldGateReleaseNow: () => boolean;
  runNftGating: () => void;
  createCustomOrderAttributes: () => AttributeInput[];
};

interface Props {
  product?: ProductType;
}

export const useReleaseGating = ({
  product,
}: Props): ReleaseGatingController => {
  const getGatedRelease = () =>
    product?.gatedRelease ? product.gatedRelease[0] : undefined;

  const isProductGated = () => {
    if (!product) return false;
    const gatedRelease = getGatedRelease();
    if (!gatedRelease || !gatedRelease?.active) {
      return false;
    }
    return ['public-release', 'draw-release', 'timed-release'].includes(
      product.releaseType,
    );
  };

  const gatedRelease = getGatedRelease();
  const requiresGating = isProductGated();
  const requiresNftGating = requiresGating && gatedRelease?.type === 'nft';

  const web3 = useWeb3Cache();
  const [passedGating, setPassedGating] = useState(!requiresGating);
  const [runNftGating, setRunNftGating] = useState(false);

  if (!product)
    return {
      requiresNftGating: false,
      passedGating: false,
      gatingLoading: false,
      onWrongChain: false,
      shouldGateReleaseNow: () => false,
      runNftGating: () => {},
      createCustomOrderAttributes: () => [],
    };

  const shouldGateReleaseNow = () => {
    if (!requiresGating) return false;

    const gatedRelease = getGatedRelease();
    if (!gatedRelease || !gatedRelease?.active) {
      return false;
    }

    // draw is not active
    if (
      product.releaseType === 'draw-release' &&
      !isLaunchDateUpcoming(product.launchDate)
    ) {
      return false;
    }

    return true;
  };

  const createCustomOrderAttributes = (): AttributeInput[] => {
    const attributes: AttributeInput[] = [];
    if (requiresNftGating && passedGating && web3.walletAddress) {
      attributes.push({
        key: 'web3WalletAddress',
        value: web3.walletAddress,
      });
    }
    return attributes;
  };

  const onWrongChain = !isSameChainId(web3.chainId!, gatedRelease?.chainId);

  const passesGatingState = useUpdatableAsyncState({
    fn: async () => {
      if (!web3.chainId) {
        return false;
      }

      return await nftsGateway.validateGating({
        chain: web3.chainId,
        contractAddress: gatedRelease!.nftContract,
        walletAddress: web3.walletAddress!,
        gatingQuery: gatedRelease?.nftTokensRange!,
      });
    },
    requirements:
      !!web3.chainId &&
      !!web3.walletAddress &&
      !!web3.web3Provider &&
      !onWrongChain &&
      runNftGating === true,
    deps: [
      web3.chainId,
      web3.walletAddress,
      web3.web3Provider,
      runNftGating,
      onWrongChain,
    ],
    defaultValue: false,
  });

  useEffect(
    () => setPassedGating(passesGatingState?.value),
    [passesGatingState.value],
  );

  return {
    product,
    gatedRelease,
    requiresNftGating,
    passedGating,
    gatingLoading: !passesGatingState.settled,
    onWrongChain,
    runNftGating: () => {
      setRunNftGating(true);
    },
    shouldGateReleaseNow,
    createCustomOrderAttributes,
  };
};
